// frontend/src/components/ErrorBoundary.jsx
//
// Catches render errors so one broken screen does not take down the whole app.
//
// WHY THIS EXISTS. React unmounts the ENTIRE tree when a render throws and
// nothing catches it. On 11 August a single undefined variable in
// SalesOrders.jsx (`so.status` inside a map over `o`) turned into a white page
// across the whole CRM — sidebar, header, everything — and `npm run build` had
// reported "Compiled successfully" beforehand. A boundary turns that same bug
// into one panel saying something went wrong, with the rest of the app still
// usable.
//
// This must be a class. There is no hook equivalent of componentDidCatch, and
// there is unlikely to be one — it is the one place React still requires a
// class component.
//
// It catches errors thrown while RENDERING. It does not catch errors inside
// event handlers or async callbacks (a failing fetch in an onClick), because
// those do not break the render tree — they are still the caller's job to
// handle.
import React from 'react';
import { Box, Paper, Typography, Button, Stack } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept as console.error deliberately: it is what the rest of the app uses
    // today, and it is what shows up in the browser console someone will be
    // looking at. When structured logging lands (backlog B2) this is the hook
    // to ship it somewhere.
    console.error('Render error caught by ErrorBoundary:', error, info?.componentStack);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    // A deliberately plain panel using the app's own theme, so a failure looks
    // like part of the product rather than a browser error page.
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 640,
            width: '100%',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Stack spacing={2} alignItems="flex-start">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ErrorOutlineIcon color="error" />
              <Typography variant="h6">This screen could not be displayed</Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Something went wrong while loading this page. The rest of the
              application is still working — use the menu to go somewhere else,
              or try again.
            </Typography>

            {/* The message is shown because the people using this are a small
                internal team who will relay it; it makes a bug report useful
                instead of "it broke". The stack stays in the console. */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                width: '100%',
                bgcolor: 'grey.50',
                fontFamily: 'monospace',
                fontSize: 13,
                color: 'text.secondary',
                overflowX: 'auto',
              }}
            >
              {this.state.error?.message || String(this.state.error)}
            </Paper>

            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleRetry}>
                Try again
              </Button>
              <Button variant="outlined" onClick={() => window.location.reload()}>
                Reload the page
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
